import React, { useCallback, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

interface CardRuleMetadata {
  complexes?: string[];
  buildings?: number[];
  floors?: number[];
  totalUnits?: number;
}

interface CardRuleData {
  complexIds?: number[];
  buildingIds?: number[];
  generatedAt?: string;
  batchId?: string;
  unitsPerCard?: number;
  sequentialOddEven?: boolean;
  allowOverflowOnLine?: boolean;
  buildingSkipStrategy?: 'none' | 'alternate' | 'custom';
  buildingTypeRules?: {
    [key: string]: unknown;
  };
  metadata?: CardRuleMetadata;
  [key: string]: unknown;
}

interface Card {
  id: number;
  name: string;
  createdAt: string;
  ruleJson: string;
  ruleData: CardRuleData & { error?: string };
  unitCount: number;
}

interface CardsResponse {
  message: string;
  data: Card[];
  count: number;
}

interface Complex {
  id: number;
  name: string;
  code: number;
  groupName?: string | null;
  _count: {
    buildings: number;
  };
}

interface ComplexesResponse {
  message: string;
  data: Complex[];
  count: number;
}

interface CardGenerationRequest {
  complexIds?: number[];
  buildingIds?: number[];
  rules?: {
    unitsPerCard?: number;
    shuffleMethod?: 'none';
    sequentialOddEven?: boolean;
    allowOverflowOnLine?: boolean;
    buildingSkipStrategy?: 'none' | 'alternate' | 'custom';
    buildingTypeRules?: {
      general?: {
        skipFloors?: number;
        unitsPerFloor?: number;
        startFromTop?: boolean;
      };
      intercom?: {
        unitsPerBuilding?: number;
        floorInterval?: number;
        preventDuplicateBuildings?: boolean;
      };
      villa?: {
        maxFloors?: number;
        selectEntireLine?: boolean;
        entranceStrategy?: 'all' | 'alternate';
        seniorFriendlySplit?: boolean;
      };
      corridor?: {
        consecutiveUnits?: number;
        floorCount?: number;
        startUnit?: 'odd' | 'even' | number;
      };
      officetel?: {
        visitEvery?: number;
        exceptions?: string[];
        appliesTo?: 'all' | string[];
        overrides?: Record<
          string,
          {
            visitEvery?: number;
            exceptions?: string[];
          }
        >;
      };
    };
  };
}

type GroupSortKey = 'createdAt' | 'cardCount' | 'complexName';
type CardSortKey = 'createdAt' | 'name' | 'unitCount';
type SortDirection = 'asc' | 'desc';

interface CardGroup {
  key: string;
  batchId?: string;
  generatedAt?: string;
  cards: Card[];
  complexIds: number[];
  complexNames: string[];
  buildingIds: number[];
  ruleSample?: CardRuleData;
  earliestCreatedAt: number;
  latestCreatedAt: number;
}

type MutableCardGroup = {
  cards: Card[];
  complexIds: Set<number>;
  complexNames: Set<string>;
  buildingIds: Set<number>;
  batchId?: string;
  generatedAt?: string;
  earliestCreatedAt: number;
  latestCreatedAt: number;
  ruleSample?: CardRuleData;
};

const GROUP_SORT_OPTIONS: Array<{ value: GroupSortKey; label: string }> = [
  { value: 'createdAt', label: '생성일' },
  { value: 'complexName', label: '단지명' },
  { value: 'cardCount', label: '카드 수' },
];

const CARD_SORT_OPTIONS: Array<{ value: CardSortKey; label: string }> = [
  { value: 'createdAt', label: '생성일' },
  { value: 'name', label: '카드명' },
  { value: 'unitCount', label: '세대 수' },
];

const fetchCards = async (): Promise<CardsResponse> => {
  const response = await fetch('/api/cards');
  if (!response.ok) {
    throw new Error('카드 목록을 불러오는데 실패했습니다');
  }
  return response.json();
};

const fetchComplexes = async (): Promise<ComplexesResponse> => {
  const response = await fetch('/api/complexes');
  if (!response.ok) {
    throw new Error('단지 목록을 불러오는데 실패했습니다');
  }
  return response.json();
};

const generateCards = async (request: CardGenerationRequest) => {
  const response = await fetch('/api/cards/generate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || '카드 생성에 실패했습니다');
  }

  return response.json();
};

const deleteCard = async (cardId: number) => {
  const response = await fetch(`/api/cards/${cardId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || '카드 삭제에 실패했습니다');
  }

  return response.json();
};

const deleteBulkCards = async (cardIds: number[]) => {
  const response = await fetch('/api/cards/bulk', {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ cardIds }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || '카드 일괄 삭제에 실패했습니다');
  }

  return response.json();
};

const CardsPage: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedComplexIds, setSelectedComplexIds] = useState<number[]>([]);
  const [unitsPerCard, setUnitsPerCard] = useState(50);
  const [sequentialOddEven, setSequentialOddEven] = useState(false);
  const [allowOverflowOnLine, setAllowOverflowOnLine] = useState(false);
  const [selectedCardIds, setSelectedCardIds] = useState<number[]>([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [groupSortKey, setGroupSortKey] = useState<GroupSortKey>('createdAt');
  const [groupSortDirection, setGroupSortDirection] = useState<SortDirection>('desc');
  const [cardSortKey, setCardSortKey] = useState<CardSortKey>('createdAt');
  const [cardSortDirection, setCardSortDirection] = useState<SortDirection>('desc');
  
  // New rule configuration state
  const [buildingSkipStrategy, setBuildingSkipStrategy] = useState<'none' | 'alternate' | 'custom'>('none');
  
  // Building type specific rules
  const [generalRules, setGeneralRules] = useState({
    skipFloors: 2,
    unitsPerFloor: 1,
    startFromTop: true,
  });
  
  const [intercomRules, setIntercomRules] = useState({
    unitsPerBuilding: 3,
    floorInterval: 1,
    preventDuplicateBuildings: false,
  });
  
  const [villaRules, setVillaRules] = useState({
    maxFloors: 5,
    selectEntireLine: true,
    entranceStrategy: 'all' as 'all' | 'alternate',
    seniorFriendlySplit: false,
  });
  
  const [corridorRules, setCorridorRules] = useState({
    consecutiveUnits: 5,
    floorCount: 5,
    startUnit: 'odd' as 'odd' | 'even' | number,
  });

  const [officetelVisitEvery, setOfficetelVisitEvery] = useState(2);
  const [officetelExceptionsInput, setOfficetelExceptionsInput] = useState('-1,-2,한자리,끝호수');
  const [officetelOverrideLinesInput, setOfficetelOverrideLinesInput] = useState('804,805');
  const [officetelOverrideVisitEvery, setOfficetelOverrideVisitEvery] = useState(1);

  const queryClient = useQueryClient();

  // Helper function to reset form state
  const resetFormState = () => {
    setSelectedComplexIds([]);
    setUnitsPerCard(50);
    setSequentialOddEven(false);
    setAllowOverflowOnLine(false);
    setBuildingSkipStrategy('none');
    setGeneralRules({ skipFloors: 2, unitsPerFloor: 1, startFromTop: true });
    setIntercomRules({ unitsPerBuilding: 3, floorInterval: 1, preventDuplicateBuildings: false });
    setVillaRules({ maxFloors: 5, selectEntireLine: true, entranceStrategy: 'all', seniorFriendlySplit: false });
    setCorridorRules({ consecutiveUnits: 5, floorCount: 5, startUnit: 'odd' });
    setOfficetelVisitEvery(2);
    setOfficetelExceptionsInput('-1,-2,한자리,끝호수');
    setOfficetelOverrideLinesInput('804,805');
    setOfficetelOverrideVisitEvery(1);
  };

  const {
    data: cardsData,
    isLoading: cardsLoading,
    error: cardsError,
  } = useQuery({
    queryKey: ['cards'],
    queryFn: fetchCards,
  });

  const { data: complexesData } = useQuery({
    queryKey: ['complexes'],
    queryFn: fetchComplexes,
  });

  const generateCardsMutation = useMutation({
    mutationFn: generateCards,
    onSuccess: data => {
      toast.success(
        `${data.data.length}개의 카드가 성공적으로 생성되었습니다!`
      );
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      setShowCreateModal(false);
      resetFormState();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const deleteCardMutation = useMutation({
    mutationFn: deleteCard,
    onSuccess: data => {
      toast.success(`"${data.data.name}" 카드가 삭제되었습니다.`);
      queryClient.invalidateQueries({ queryKey: ['cards'] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const deleteBulkCardsMutation = useMutation({
    mutationFn: deleteBulkCards,
    onSuccess: data => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      setSelectedCardIds([]);
      setIsSelectionMode(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const complexNameMap = useMemo(() => {
    const map = new Map<number, string>();
    if (complexesData?.data) {
      complexesData.data.forEach(complex => {
        map.set(complex.id, complex.name);
      });
    }
    return map;
  }, [complexesData?.data]);

  const complexGroups = useMemo(() => {
    if (!complexesData?.data) {
      return [];
    }
    const groups = new Map<string, Complex[]>();
    complexesData.data.forEach(complex => {
      const key = complex.groupName?.trim() || '';
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(complex);
    });

    return Array.from(groups.entries())
      .map(([name, complexes]) => ({
        name,
        label: name ? name : '그룹 없음',
        complexes,
      }))
      .sort((a, b) => {
        if (!a.name && !b.name) return 0;
        if (!a.name) return 1;
        if (!b.name) return -1;
        return a.name.localeCompare(b.name, 'ko');
      });
  }, [complexesData?.data]);

  const selectedComplexIdSet = useMemo(
    () => new Set(selectedComplexIds),
    [selectedComplexIds]
  );

  const toggleComplexGroup = useCallback(
    (groupComplexIds: number[]) => {
      setSelectedComplexIds(prev => {
        const isFullySelected = groupComplexIds.every(id => prev.includes(id));
        if (isFullySelected) {
          return prev.filter(id => !groupComplexIds.includes(id));
        }
        const combined = new Set(prev);
        groupComplexIds.forEach(id => combined.add(id));
        return Array.from(combined);
      });
    },
    [setSelectedComplexIds]
  );

  const hasNamedGroups = useMemo(
    () => complexGroups.some(group => group.name),
    [complexGroups]
  );

  const resolveGroupKey = (card: Card): string => {
    const ruleData = card.ruleData?.error ? {} : card.ruleData || {};
    if (ruleData.batchId && typeof ruleData.batchId === 'string') {
      return ruleData.batchId;
    }
    const generatedAt = ruleData.generatedAt || card.createdAt;
    const complexKey = Array.isArray(ruleData.complexIds)
      ? [...ruleData.complexIds].sort((a, b) => a - b).join('-')
      : '';
    return `legacy-${generatedAt}-${complexKey}`;
  };

  const cardGroups = useMemo<CardGroup[]>(() => {
    if (!cardsData?.data) {
      return [];
    }

    const groupMap = new Map<string, MutableCardGroup>();

    cardsData.data.forEach(card => {
      const groupKey = resolveGroupKey(card);
      const cardCreatedAt = new Date(card.createdAt).getTime();
      const ruleData = card.ruleData?.error ? {} : card.ruleData || {};

      const complexIdsFromRule = Array.isArray(ruleData.complexIds)
        ? ruleData.complexIds
        : [];
      const buildingIdsFromRule = Array.isArray(ruleData.buildingIds)
        ? ruleData.buildingIds
        : [];
      const metadataComplexNames = Array.isArray(ruleData.metadata?.complexes)
        ? ruleData.metadata?.complexes ?? []
        : [];
      const metadataBuildings = Array.isArray(ruleData.metadata?.buildings)
        ? ruleData.metadata?.buildings ?? []
        : [];

      const resolvedComplexNames = new Set<string>();
      complexIdsFromRule.forEach(id => {
        const name = complexNameMap.get(id);
        if (name) {
          resolvedComplexNames.add(name);
        }
      });
      metadataComplexNames.forEach(name => {
        if (typeof name === 'string') {
          resolvedComplexNames.add(name);
        }
      });

      const targetGroup = groupMap.get(groupKey);

      if (!targetGroup) {
        const newGroup: MutableCardGroup = {
          cards: [card],
          complexIds: new Set<number>(complexIdsFromRule),
          complexNames: resolvedComplexNames,
          buildingIds: new Set<number>([
            ...buildingIdsFromRule,
            ...metadataBuildings.filter((value): value is number => typeof value === 'number'),
          ]),
          earliestCreatedAt: cardCreatedAt,
          latestCreatedAt: cardCreatedAt,
        };
        if (typeof ruleData.batchId === 'string') {
          newGroup.batchId = ruleData.batchId;
        }
        if (typeof ruleData.generatedAt === 'string') {
          newGroup.generatedAt = ruleData.generatedAt;
        }
        if (Object.keys(ruleData).length > 0) {
          newGroup.ruleSample = ruleData;
        }
        groupMap.set(groupKey, newGroup);
        return;
      }

      targetGroup.cards.push(card);
      complexIdsFromRule.forEach(id => targetGroup.complexIds.add(id));
      buildingIdsFromRule.forEach(id => targetGroup.buildingIds.add(id));
      metadataBuildings
        .filter((value): value is number => typeof value === 'number')
        .forEach(id => targetGroup.buildingIds.add(id));
      resolvedComplexNames.forEach(name => targetGroup.complexNames.add(name));
      if (!targetGroup.batchId && typeof ruleData.batchId === 'string') {
        targetGroup.batchId = ruleData.batchId;
      }
      if (!targetGroup.generatedAt && typeof ruleData.generatedAt === 'string') {
        targetGroup.generatedAt = ruleData.generatedAt;
      }
      if (!targetGroup.ruleSample && Object.keys(ruleData).length > 0) {
        targetGroup.ruleSample = ruleData;
      }
      targetGroup.earliestCreatedAt = Math.min(targetGroup.earliestCreatedAt, cardCreatedAt);
      targetGroup.latestCreatedAt = Math.max(targetGroup.latestCreatedAt, cardCreatedAt);
    });

    return Array.from(groupMap.entries()).map(([key, value]) => {
      const group: CardGroup = {
        key,
        cards: value.cards,
        complexIds: Array.from(value.complexIds),
        complexNames: Array.from(value.complexNames),
        buildingIds: Array.from(value.buildingIds),
        earliestCreatedAt: value.earliestCreatedAt,
        latestCreatedAt: value.latestCreatedAt,
      };
      if (value.batchId !== undefined) {
        group.batchId = value.batchId;
      }
      if (value.generatedAt !== undefined) {
        group.generatedAt = value.generatedAt;
      }
      if (value.ruleSample !== undefined) {
        group.ruleSample = value.ruleSample;
      }
      return group;
    });
  }, [cardsData?.data, complexNameMap]);

  const sortedGroups = useMemo(() => {
    const groupsCopy = [...cardGroups];
    const multiplier = groupSortDirection === 'asc' ? 1 : -1;

    groupsCopy.sort((a, b) => {
      let comparison = 0;

      switch (groupSortKey) {
        case 'cardCount':
          comparison = a.cards.length - b.cards.length;
          break;
        case 'complexName': {
          const aName = a.complexNames.join(', ') || '미확인 단지';
          const bName = b.complexNames.join(', ') || '미확인 단지';
          comparison = aName.localeCompare(bName, 'ko');
          break;
        }
        case 'createdAt':
        default: {
          const aTime = a.generatedAt ? new Date(a.generatedAt).getTime() : a.latestCreatedAt;
          const bTime = b.generatedAt ? new Date(b.generatedAt).getTime() : b.latestCreatedAt;
          comparison = aTime - bTime;
        }
      }

      return comparison * multiplier;
    });

    return groupsCopy;
  }, [cardGroups, groupSortDirection, groupSortKey]);

  const allCardIds = useMemo(
    () => cardGroups.flatMap(group => group.cards.map(card => card.id)),
    [cardGroups]
  );

  const handleGenerateCards = () => {
    if (selectedComplexIds.length === 0) {
      toast.error('최소 하나의 단지를 선택해주세요');
      return;
    }

    const parsedExceptions = officetelExceptionsInput
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
    const exceptions =
      parsedExceptions.length > 0 ? parsedExceptions : ['-1', '-2', '한자리', '끝호수'];

    const overrideLines = officetelOverrideLinesInput
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
    const overrideVisitEvery = Math.max(1, officetelOverrideVisitEvery || 1);
    const overrides =
      overrideLines.length > 0
        ? overrideLines.reduce<Record<string, { visitEvery?: number; exceptions?: string[] }>>(
            (acc, line) => {
              acc[line] = { visitEvery: overrideVisitEvery };
              return acc;
            },
            {}
          )
        : undefined;

    const officetelRule = {
      visitEvery: Math.max(1, officetelVisitEvery || 2),
      exceptions,
      appliesTo: 'all' as const,
      overrides: overrides ?? {},
    };

    const rules: CardGenerationRequest['rules'] = {
      unitsPerCard,
      shuffleMethod: 'none',
      sequentialOddEven,
      allowOverflowOnLine,
      buildingSkipStrategy,
      buildingTypeRules: {
        general: generalRules,
        intercom: intercomRules,
        villa: villaRules,
        corridor: corridorRules,
        officetel: officetelRule ?? undefined,
      },
    };

    generateCardsMutation.mutate({
      complexIds: selectedComplexIds,
      rules,
    });
  };

  const handleDeleteCard = (cardId: number, cardName: string) => {
    if (window.confirm(`"${cardName}" 카드를 삭제하시겠습니까?`)) {
      deleteCardMutation.mutate(cardId);
    }
  };

  const handleBulkDelete = () => {
    if (selectedCardIds.length === 0) {
      toast.error('삭제할 카드를 선택해주세요');
      return;
    }

    if (
      window.confirm(
        `선택한 ${selectedCardIds.length}개의 카드를 삭제하시겠습니까?`
      )
    ) {
      deleteBulkCardsMutation.mutate(selectedCardIds);
    }
  };

  const handleCardSelection = (cardId: number, isSelected: boolean) => {
    if (isSelected) {
      setSelectedCardIds([...selectedCardIds, cardId]);
    } else {
      setSelectedCardIds(selectedCardIds.filter(id => id !== cardId));
    }
  };

  const handleSelectAll = () => {
    if (allCardIds.length === 0) {
      return;
    }

    if (selectedCardIds.length === allCardIds.length) {
      setSelectedCardIds([]);
    } else {
      setSelectedCardIds(allCardIds);
    }
  };

  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedCardIds([]);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getSortedCards = (cards: Card[]) => {
    const multiplier = cardSortDirection === 'asc' ? 1 : -1;
    const cardsCopy = [...cards];

    cardsCopy.sort((a, b) => {
      let comparison = 0;

      switch (cardSortKey) {
        case 'name':
          comparison = a.name.localeCompare(b.name, 'ko');
          break;
        case 'unitCount':
          comparison = a.unitCount - b.unitCount;
          break;
        case 'createdAt':
        default: {
          const aTime = new Date(a.createdAt).getTime();
          const bTime = new Date(b.createdAt).getTime();
          comparison = aTime - bTime;
        }
      }

      return comparison * multiplier;
    });

    return cardsCopy;
  };

  if (cardsError) {
    return (
      <div className="page-wide">
        <div className="card card-body text-center">
          <h1 className="page-title mb-2">오류</h1>
          <p className="text-red-600 text-sm">카드를 불러오는데 실패했습니다. 다시 시도해주세요.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wide">
      <div className="card p-4 sm:p-6">
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="page-title">방문 카드</h1>
              {cardsData && (
                <p className="text-gray-600 mt-1">총 {cardsData.count}개 카드</p>
              )}
            </div>
            <div className="flex gap-2">
              {cardsData && cardsData.count > 0 && (
                <button
                  onClick={toggleSelectionMode}
                  className={`font-bold py-2 px-4 rounded ${
                    isSelectionMode
                      ? 'bg-gray-500 hover:bg-gray-700 text-white'
                      : 'bg-orange-500 hover:bg-orange-700 text-white'
                  }`}
                >
                  {isSelectionMode ? '선택 취소' : '일괄 삭제'}
                </button>
              )}
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              >
                새 카드 생성
              </button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 justify-end">
            <div className="flex items-center gap-2">
              <label htmlFor="group-sort" className="text-sm text-gray-500">
                그룹 정렬
              </label>
              <select
                id="group-sort"
                value={groupSortKey}
                onChange={event => setGroupSortKey(event.target.value as GroupSortKey)}
                className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {GROUP_SORT_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() =>
                  setGroupSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
                }
                className="text-sm px-2 py-1 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
              >
                {groupSortDirection === 'asc' ? '오름차순' : '내림차순'}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="card-sort" className="text-sm text-gray-500">
                카드 정렬
              </label>
              <select
                id="card-sort"
                value={cardSortKey}
                onChange={event => setCardSortKey(event.target.value as CardSortKey)}
                className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {CARD_SORT_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() =>
                  setCardSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
                }
                className="text-sm px-2 py-1 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
              >
                {cardSortDirection === 'asc' ? '오름차순' : '내림차순'}
              </button>
            </div>
          </div>
        </div>

        {/* Bulk selection controls */}
        {isSelectionMode && allCardIds.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedCardIds.length === allCardIds.length}
                    onChange={handleSelectAll}
                    className="rounded"
                    disabled={allCardIds.length === 0}
                  />
                  <span className="text-sm font-medium">
                    전체 선택 ({selectedCardIds.length}/{allCardIds.length})
                  </span>
                </label>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleBulkDelete}
                  disabled={
                    selectedCardIds.length === 0 ||
                    deleteBulkCardsMutation.isPending
                  }
                  className="bg-red-500 hover:bg-red-700 text-white text-sm py-1 px-3 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteBulkCardsMutation.isPending
                    ? '삭제 중...'
                    : `선택한 ${selectedCardIds.length}개 삭제`}
                </button>
              </div>
            </div>
          </div>
        )}

        {cardsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="border rounded-lg p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2 mb-4"></div>
                  <div className="flex gap-2">
                    <div className="h-8 bg-gray-200 rounded w-20"></div>
                    <div className="h-8 bg-gray-200 rounded w-24"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : cardGroups.length === 0 ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <p className="text-gray-500 mb-4">카드가 없습니다</p>
            <p className="text-sm text-gray-400 mb-4">
              첫 번째 방문 카드를 생성하여 시작하세요
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded"
            >
              첫 카드 생성
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {sortedGroups.map(group => {
              const groupTitle =
                group.complexNames.length > 0
                  ? group.complexNames.join(', ')
                  : '단지 정보 없음';
              const sortedBuildingIds = [...group.buildingIds].sort((a, b) => a - b);
              const buildingLabel =
                sortedBuildingIds.length > 0
                  ? sortedBuildingIds.map(id => `${id}동`).join(', ')
                  : null;
              const generatedLabel = group.generatedAt
                ? formatDateTime(group.generatedAt)
                : formatDateTime(new Date(group.latestCreatedAt).toISOString());
              const ruleSample = group.ruleSample;
              const ruleSummaryParts: string[] = [];

              if (typeof ruleSample?.unitsPerCard === 'number') {
                ruleSummaryParts.push(`카드당 ${ruleSample.unitsPerCard}세대`);
              }
              if (ruleSample?.sequentialOddEven) {
                ruleSummaryParts.push('홀짝 분리 적용');
                if (ruleSample.allowOverflowOnLine) {
                  ruleSummaryParts.push('라인 끝까지 배정');
                }
              }
              if (
                ruleSample?.buildingSkipStrategy &&
                ruleSample.buildingSkipStrategy !== 'none'
              ) {
                const label =
                  ruleSample.buildingSkipStrategy === 'alternate'
                    ? '동 건너뛰기: 교대로'
                    : '동 건너뛰기: 사용자 지정';
                ruleSummaryParts.push(label);
              }

              const officetelRule = ruleSample?.buildingTypeRules?.officetel as
                | { visitEvery?: number; overrides?: Record<string, unknown> }
                | undefined;
              if (officetelRule?.visitEvery) {
                ruleSummaryParts.push(`오피스텔 간격 ${officetelRule.visitEvery}호`);
                const overrideLines = officetelRule.overrides
                  ? Object.keys(officetelRule.overrides)
                  : [];
                if (overrideLines.length > 0) {
                  const preview = overrideLines.slice(0, 3).join(', ');
                  const suffix = overrideLines.length > 3 ? '…' : '';
                  ruleSummaryParts.push(`오버라이드: ${preview}${suffix}`);
                }
              }

              const ruleSummary = ruleSummaryParts.join(' · ');

              return (
                <div
                  key={group.key}
                  className="border rounded-lg p-6 hover:shadow-sm transition-shadow"
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {groupTitle}
                      </h2>
                      <div className="mt-1 text-sm text-gray-600 flex flex-wrap gap-x-3 gap-y-1">
                        <span>생성 일시: {generatedLabel}</span>
                        <span>카드 수: {group.cards.length}개</span>
                        {buildingLabel && <span>동: {buildingLabel}</span>}
                      </div>
                      {ruleSummary && (
                        <p className="mt-1 text-sm text-gray-500">
                          규칙: {ruleSummary}
                        </p>
                      )}
                    </div>
                    {group.batchId && (
                      <div className="text-xs text-gray-400">
                        그룹 ID: {group.batchId}
                      </div>
                    )}
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {getSortedCards(group.cards).map(card => (
                      <div
                        key={card.id}
                        className={`border rounded-lg p-6 hover:shadow-md transition-shadow ${
                          isSelectionMode && selectedCardIds.includes(card.id)
                            ? 'ring-2 ring-blue-500 bg-blue-50'
                            : ''
                        }`}
                      >
                        {isSelectionMode && (
                          <div className="flex items-center mb-4">
                            <input
                              type="checkbox"
                              checked={selectedCardIds.includes(card.id)}
                              onChange={e =>
                                handleCardSelection(card.id, e.target.checked)
                              }
                              className="rounded mr-2"
                            />
                            <span className="text-sm text-gray-600">선택</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold">{card.name}</h3>
                          <span className="text-sm text-gray-500">
                            {card.unitCount}개 세대
                          </span>
                        </div>
                        <p className="text-gray-600 mb-4">
                          생성일: {formatDate(card.createdAt)}
                        </p>
                        {card.ruleData && !card.ruleData.error && (
                          <div className="text-sm text-gray-500 mb-4">
                            <p>
                              카드당 세대 수:{' '}
                              {typeof card.ruleData.unitsPerCard === 'number'
                                ? card.ruleData.unitsPerCard
                                : '없음'}
                            </p>
                          </div>
                        )}
                        <div className="flex gap-2 flex-wrap">
                          <Link
                            to={`/cards/${card.id}`}
                            className="bg-green-500 hover:bg-green-700 text-white text-sm py-1 px-3 rounded inline-block"
                          >
                            상세 보기
                          </Link>
                          <button
                            onClick={() => {
                              toast('카드 상세 페이지에서 CSV 다운로드 가능');
                            }}
                            className="bg-gray-500 hover:bg-gray-700 text-white text-sm py-1 px-3 rounded"
                          >
                            CSV 다운로드
                          </button>
                          {!isSelectionMode && (
                            <button
                              onClick={() => handleDeleteCard(card.id, card.name)}
                              disabled={deleteCardMutation.isPending}
                              className="bg-red-500 hover:bg-red-700 text-white text-sm py-1 px-3 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {deleteCardMutation.isPending ? '삭제 중...' : '삭제'}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Card Modal */}
      {showCreateModal ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">새 방문 카드 생성</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetFormState();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  단지 선택
                </label>
                {hasNamedGroups && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {complexGroups
                      .filter(group => group.name)
                      .map(group => {
                        const groupIds = group.complexes.map(complex => complex.id);
                        const isSelected = groupIds.every(id => selectedComplexIdSet.has(id));
                        const isPartial =
                          !isSelected && groupIds.some(id => selectedComplexIdSet.has(id));
                        const baseClass =
                          'px-3 py-1 text-xs rounded-full border transition-colors';
                        const stateClass = isSelected
                          ? 'bg-indigo-500 border-indigo-500 text-white'
                          : isPartial
                          ? 'bg-indigo-50 border-indigo-200 text-indigo-600'
                          : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50';
                        return (
                          <button
                            key={group.name}
                            type="button"
                            onClick={() => toggleComplexGroup(groupIds)}
                            className={`${baseClass} ${stateClass}`}
                          >
                            {group.label} ({group.complexes.length})
                          </button>
                        );
                      })}
                  </div>
                )}
                <div className="max-h-48 overflow-y-auto border rounded-md p-3 space-y-3">
                  {complexGroups.length === 0 && (
                    <p className="text-sm text-gray-500">선택할 수 있는 단지가 없습니다.</p>
                  )}
                  {complexGroups.map(group => (
                    <div key={group.name || 'ungrouped'}>
                      <p className="text-xs font-semibold text-gray-500 mb-2">
                        {group.label}
                      </p>
                      <div className="space-y-2">
                        {group.complexes.map(complex => (
                          <label
                            key={complex.id}
                            className="flex items-center gap-2 text-sm"
                          >
                            <input
                              type="checkbox"
                              checked={selectedComplexIdSet.has(complex.id)}
                              onChange={e => {
                                const checked = e.target.checked;
                                setSelectedComplexIds(prev => {
                                  const next = new Set(prev);
                                  if (checked) {
                                    next.add(complex.id);
                                  } else {
                                    next.delete(complex.id);
                                  }
                                  return Array.from(next);
                                });
                              }}
                              className="rounded"
                            />
                            <span>
                              {complex.name}
                              <span className="text-gray-400 text-xs ml-2">
                                {complex._count.buildings}개 동
                              </span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {selectedComplexIds.length === 0 && (
                  <p className="text-red-500 text-sm mt-1">최소 하나의 단지를 선택해주세요</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    카드당 세대 수
                  </label>
                  <input
                    type="number"
                    value={unitsPerCard}
                    onChange={e => setUnitsPerCard(parseInt(e.target.value) || 50)}
                    min="1"
                    max="200"
                    className="w-full border rounded-md px-3 py-2"
                    placeholder="카드당 세대 수를 입력하세요"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    옵션
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={sequentialOddEven}
                      onChange={e => {
                        const checked = e.target.checked;
                        setSequentialOddEven(checked);
                        if (!checked) {
                          setAllowOverflowOnLine(false);
                        }
                      }}
                      className="rounded"
                    />
                    <div className="flex flex-col">
                      <span className="text-sm">같은 라인 우선 생성 (세로 같은 호수)</span>
                      <span className="text-xs text-gray-500">1501→1401→1301 순서로 같은 호수를 우선 선택</span>
                    </div>
                  </label>
                  <label className="flex items-start space-x-2 mt-3">
                    <input
                      type="checkbox"
                      checked={allowOverflowOnLine}
                      onChange={e => setAllowOverflowOnLine(e.target.checked)}
                      className="rounded mt-1"
                      disabled={!sequentialOddEven}
                    />
                    <div className="flex flex-col">
                      <span className={`text-sm ${!sequentialOddEven ? 'text-gray-400' : ''}`}>
                        같은 라인을 끝까지 채우기
                      </span>
                      <span className="text-xs text-gray-500">
                        같은 라인(1501→1401→1301 등)이 카드당 최대 세대 수를 넘어도 모두 배정합니다.
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-md font-semibold text-gray-800">고급 규칙 설정</h4>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    동 건너뛰기 전략
                  </label>
                  <select
                    value={buildingSkipStrategy}
                    onChange={e => setBuildingSkipStrategy(e.target.value as 'none' | 'alternate' | 'custom')}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    <option value="none">건너뛰기 없음</option>
                    <option value="alternate">교대로 건너뛰기</option>
                    <option value="custom">사용자 정의</option>
                  </select>
                </div>

                <div className="space-y-4">
                  <h5 className="text-sm font-semibold text-gray-700">건물 유형별 규칙</h5>

                  <div className="border rounded-md p-3">
                    <h6 className="text-sm font-medium text-gray-700 mb-2">일반 아파트 규칙</h6>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">층 건너뛰기</label>
                        <input
                          type="number"
                          value={generalRules.skipFloors}
                          onChange={e => setGeneralRules({ ...generalRules, skipFloors: parseInt(e.target.value) || 2 })}
                          min="1"
                          max="10"
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">층당 세대 수</label>
                        <input
                          type="number"
                          value={generalRules.unitsPerFloor}
                          onChange={e => setGeneralRules({ ...generalRules, unitsPerFloor: parseInt(e.target.value) || 1 })}
                          min="1"
                          max="10"
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      </div>
                      <label className="flex items-center space-x-1 text-xs">
                        <input
                          type="checkbox"
                          checked={generalRules.startFromTop}
                          onChange={e => setGeneralRules({ ...generalRules, startFromTop: e.target.checked })}
                          className="rounded"
                        />
                        <span>최상층부터 시작</span>
                      </label>
                    </div>
                  </div>

                    <div className="border rounded-md p-3">
                      <h6 className="text-sm font-medium text-gray-700 mb-2">인터폰 아파트 규칙</h6>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">동당 세대 수</label>
                        <input
                          type="number"
                          value={intercomRules.unitsPerBuilding}
                          onChange={e => setIntercomRules({ ...intercomRules, unitsPerBuilding: parseInt(e.target.value) || 3 })}
                          min="1"
                          max="20"
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">층간 간격</label>
                        <input
                          type="number"
                          value={intercomRules.floorInterval}
                          onChange={e => setIntercomRules({ ...intercomRules, floorInterval: parseInt(e.target.value) || 1 })}
                          min="1"
                          max="5"
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      </div>
                    </div>
                    <label className="flex items-center space-x-2 text-xs">
                      <input
                        type="checkbox"
                        checked={intercomRules.preventDuplicateBuildings}
                        onChange={e =>
                          setIntercomRules({
                            ...intercomRules,
                            preventDuplicateBuildings: e.target.checked,
                          })
                        }
                        className="rounded"
                      />
                      <span>한 카드에 동일 동을 중복 배치하지 않음</span>
                    </label>
                    </div>

                  <div className="border rounded-md p-3 space-y-3">
                    <h6 className="text-sm font-medium text-gray-700 mb-2">계단식(빌라) 규칙</h6>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">최대 층수</label>
                        <input
                          type="number"
                          value={villaRules.maxFloors}
                          onChange={e => setVillaRules({ ...villaRules, maxFloors: parseInt(e.target.value) || 5 })}
                          min="1"
                          max="10"
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">입구 전략</label>
                        <select
                          value={villaRules.entranceStrategy}
                          onChange={e => setVillaRules({ ...villaRules, entranceStrategy: e.target.value as 'all' | 'alternate' })}
                          className="w-full border rounded px-2 py-1 text-sm"
                        >
                          <option value="all">모든 입구</option>
                          <option value="alternate">교대로</option>
                        </select>
                      </div>
                      <label className="flex items-center space-x-1 text-xs">
                        <input
                          type="checkbox"
                          checked={villaRules.selectEntireLine}
                          onChange={e => setVillaRules({ ...villaRules, selectEntireLine: e.target.checked })}
                          className="rounded"
                        />
                        <span>전체 라인 선택</span>
                      </label>
                    </div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={villaRules.seniorFriendlySplit}
                        onChange={e => setVillaRules({ ...villaRules, seniorFriendlySplit: e.target.checked })}
                        className="rounded"
                      />
                      <div className="flex flex-col">
                        <span className="text-sm">연로자 배려 모드</span>
                        <span className="text-xs text-gray-500">2층 이하 세대와 3층 이상 세대를 다른 카드에 배치합니다.</span>
                      </div>
                    </label>
                  </div>

                  <div className="border rounded-md p-3">
                    <h6 className="text-sm font-medium text-gray-700 mb-2">복도식 아파트 규칙</h6>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">연속 세대 수</label>
                        <input
                          type="number"
                          value={corridorRules.consecutiveUnits}
                          onChange={e => setCorridorRules({ ...corridorRules, consecutiveUnits: parseInt(e.target.value) || 5 })}
                          min="1"
                          max="20"
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">적용 층수</label>
                        <input
                          type="number"
                          value={corridorRules.floorCount}
                          onChange={e => setCorridorRules({ ...corridorRules, floorCount: parseInt(e.target.value) || 5 })}
                          min="1"
                          max="20"
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">시작 호수</label>
                        <select
                          value={corridorRules.startUnit}
                          onChange={e => {
                            const value = e.target.value;
                            setCorridorRules({
                              ...corridorRules,
                              startUnit: value === 'odd' || value === 'even' ? value : parseInt(value) || 'odd',
                            });
                          }}
                          className="w-full border rounded px-2 py-1 text-sm"
                        >
                          <option value="odd">홀수</option>
                          <option value="even">짝수</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-md p-3 space-y-3">
                    <h6 className="text-sm font-medium text-gray-700 mb-2">오피스텔 간격 규칙</h6>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">기본 간격(visitEvery)</label>
                        <input
                          type="number"
                          value={officetelVisitEvery}
                          onChange={e => setOfficetelVisitEvery(parseInt(e.target.value) || 2)}
                          min="1"
                          max="20"
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">예외 호수 (쉼표 구분)</label>
                        <input
                          type="text"
                          value={officetelExceptionsInput}
                          onChange={e => setOfficetelExceptionsInput(e.target.value)}
                          className="w-full border rounded px-2 py-1 text-sm"
                          placeholder="-1,-2,한자리,끝호수"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">라인 오버라이드 (쉼표 구분)</label>
                        <input
                          type="text"
                          value={officetelOverrideLinesInput}
                          onChange={e => setOfficetelOverrideLinesInput(e.target.value)}
                          className="w-full border rounded px-2 py-1 text-sm"
                          placeholder="804,805"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">오버라이드 간격</label>
                        <input
                          type="number"
                          value={officetelOverrideVisitEvery}
                          onChange={e => setOfficetelOverrideVisitEvery(parseInt(e.target.value) || 1)}
                          min="1"
                          max="20"
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      </div>
                      <p className="text-xs text-gray-500 self-center">
                        예: 불규칙 라인(804/805)은 전수조사(간격 1), 나머지는 기본 간격을 사용합니다.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetFormState();
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  onClick={handleGenerateCards}
                  disabled={
                    generateCardsMutation.isPending || selectedComplexIds.length === 0
                  }
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generateCardsMutation.isPending ? '생성 중...' : '카드 생성'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CardsPage;
